import {
  ViewModel,
  Model,
  ModelType,
  ItemApiState,
  convertToModel,
} from "coalesce-vue";
import {
  TicketPurchaseTicket,
  EventDateDto,
  TicketPurchaseAddon,
} from "@/models.g";

const staticData = useStaticData();

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatCurrency(
  amount: number | null,
  useParentheses?: boolean,
) {
  if (useParentheses && amount && amount < 0) {
    return `(${currencyFormatter.format(Math.abs(amount))})`;
  }

  return currencyFormatter.format(amount ?? 0);
}

//e.g.: MON, JUNE 9, 2025 • 5:00 PM
export function formatEventTime(dateString: string | undefined) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const dayName = date
    .toLocaleDateString("en-US", { weekday: "short" })
    .toUpperCase();
  const monthDay = date
    .toLocaleDateString("en-US", { month: "long", day: "numeric" })
    .toUpperCase();
  const year = date.getFullYear();
  const time = date
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
  return `${dayName}, ${monthDay}, ${year} • ${time}`;
}

export function isTouch() {
  return "ontouchstart" in window || (navigator.maxTouchPoints ?? 0) > 0;
}

/**
 * Detects if the current device is running iOS (iPhone, iPad, or iPod).
 *
 * @returns true if running on iOS device, false otherwise
 */
export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function formatMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60),
    minutes = totalMinutes % 60;
  let result = "";

  if (hours == 1) {
    result = "1 hour ";
  } else if (hours > 1) {
    result = hours + " hours ";
  }
  if (minutes > 0) {
    result += minutes + " minutes";
  }
  return result.trim();
}

export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Reload `target` after a very slight delay if `target` is not saving,
 * `responseSource` is not pending,
 * and nothing in `otherPendingLoaders` is pending. */
export function reloadIfNoRequestsPending<TModel extends Model<ModelType>>(
  target: ViewModel<TModel>,
  responseSource: ItemApiState<any, TModel>,
  ...otherPendingLoaders: ItemApiState<any, any>[]
) {
  // Delay slightly to allow pending debounced requests to kick off.
  setTimeout(() => {
    if (
      responseSource.result &&
      !target.$save.isLoading &&
      (!otherPendingLoaders || !otherPendingLoaders.some((l) => l.isLoading))
    ) {
      // Temp workaround for a coalesce-vue bug that isn't converting "object"s
      // to Model implementations when setting them on a ViewModel.
      // This is an issue if we're loading the result from a failed request,
      // which doesn't automatically convert the response into a Model like successes do.
      convertToModel(responseSource.result, target.$metadata);

      target.$loadCleanData(responseSource.result!);
    }
  }, 10);
}

export function setupPurchaseReloadHandling<TModel extends Model<ModelType>>(
  ticketPurchase: ViewModel<TModel>,
  responseSource: ItemApiState<any, TModel>,
  ...otherPendingLoaders: ItemApiState<any, any>[]
) {
  const cb = () =>
    reloadIfNoRequestsPending(
      ticketPurchase,
      responseSource,
      ...otherPendingLoaders,
    );

  responseSource.onFulfilled(cb);
  responseSource.onRejected(cb);
}

export function scrollToTop(
  wait: number = 200,
  behavior: ScrollBehavior = "smooth",
) {
  setTimeout(
    () => window.scrollTo({ left: 0, top: 0, behavior: behavior }),
    wait,
  );
}

// #3470: raised by SoftJourn seat-map ticket-change handlers so the purchase
// reload they trigger does not scroll the page to the top. Cleared by the
// purchase-single views in their $load.onFulfilled handler.
export const suppressPurchaseScroll = ref(false);

export function getTicketGroupings(
  tickets: TicketPurchaseTicket[],
  eventDate?: EventDateDto,
) {
  const groupings: {
    [key: string]: {
      firstTicket: TicketPurchaseTicket;
      allTickets: TicketPurchaseTicket[];
    };
  } = {};

  for (const ticket of tickets) {
    const groupingKey = [
      ticket.price,
      ticket.fees,
      ticket.subtotalWithFees,
      ticket.discountTotal,
      ticket.addons,
      ticket.salesTax,
      ticket.convenienceFee,
      ticket.totalValue,
      ticket.relatedPurchase?.publicId,
      ticket.relatedPurchase?.description,
      ticket.description,
      ticket.isReservedIndividualSeat,
      ticket.seatNumber,
      ticket.status,
    ];
    const groupingString = JSON.stringify(groupingKey);

    let grouping = groupings[groupingString];
    if (!grouping) {
      grouping = groupings[groupingString] = {
        firstTicket: ticket,
        allTickets: [],
      };
    }
    grouping.allTickets.push(ticket);
  }

  function indexOf(s: string | null) {
    return eventDate!.eventPrices!.findIndex((p) =>
      s!.startsWith(p.displayName!),
    );
  }

  // Sort the results by the position of the ticket in the
  // list of event prices for the event date.
  // This keeps the ordering consistent when picking quantities + any summary views.
  return Object.values(groupings).sort((a, b) => {
    // First, sort by isReservedIndividualSeat (true values first)
    const aIsReserved = a.firstTicket.isReservedIndividualSeat ? 1 : 0;
    const bIsReserved = b.firstTicket.isReservedIndividualSeat ? 1 : 0;

    if (aIsReserved !== bIsReserved) {
      return bIsReserved - aIsReserved; // Reserved seats come first
    }
    if (eventDate) {
      return (
        indexOf(a.firstTicket.description) - indexOf(b.firstTicket.description)
      );
    }

    return a.firstTicket.description!.localeCompare(b.firstTicket.description!);
  });
}

export function getAddonGroupings(addons: TicketPurchaseAddon[]) {
  const groupings: {
    [key: string]: {
      firstAddon: TicketPurchaseAddon;
      allAddons: TicketPurchaseAddon[];
    };
  } = {};

  for (const addon of addons) {
    const groupingKey = [
      addon.name,
      addon.price,
      addon.salesTax,
      addon.convenienceFee,
      addon.refundedPrice,
      addon.relatedPurchase?.publicId,
      addon.relatedPurchase?.description,
      addon.marketingDescription,
      addon.status,
    ];
    const groupingString = JSON.stringify(groupingKey);

    let grouping = groupings[groupingString];
    if (!grouping) {
      grouping = groupings[groupingString] = {
        firstAddon: addon,
        allAddons: [],
      };
    }
    grouping.allAddons.push(addon);
  }

  return Object.values(groupings).sort((a, b) => {
    return a.firstAddon.name!.localeCompare(b.firstAddon.name!);
  });
}

import DOMPurify from "dompurify";

export function sanitizeHtml(html: string): string {
  const sanitized = DOMPurify.sanitize(html);
  return sanitized;
}

/**
 * Strips all HTML tags from a string and returns plain text only.
 * Uses DOMParser for reliable HTML parsing and text extraction.
 *
 * @param html - HTML string to strip tags from
 * @returns Plain text content without HTML markup
 */
export function stripHtml(html: string): string {
  if (!html) return "";

  // Use DOMParser to parse HTML and extract text content
  const doc = new DOMParser().parseFromString(html, "text/html");
  const text = doc.body.textContent || "";

  // Trim and normalize whitespace
  return text.trim();
}

import { format, parse, isValid } from "date-fns";
export class EventDatesParameters {
  // Use the tenant default setting, default to list if for whatever reason it's not available.
  // Same deal for the calendar type, default to month.
  public tab: "list" | "calendar" | "grid";
  public calendarType: "month" | "day" =
    staticData.DEFAULT_CALENDAR_VIEW ?? "month";

  constructor(defaultTab?: "list" | "calendar" | "grid") {
    const tab = defaultTab ?? staticData.DEFAULT_LIST_VIEW ?? "list";
    // The grid tab is only rendered when the tenant enables grid view, so a
    // configured grid default would otherwise leave the user on a tab they
    // can't see selected and can't get back to.
    this.tab = tab == "grid" && !staticData.ENABLE_GRID_VIEW ? "list" : tab;
  }

  public get mode(): "calendar-day" | "calendar-month" | "list" | "grid" {
    return (this.tab +
      (this.tab == "calendar" ? "-" + this.calendarType : "")) as any;
  }
  public set mode(val) {
    const parts = val.split("-");
    this.tab = parts[0] as any;
    this.calendarType = parts[1] || (this.calendarType as any);
  }

  public calendarDate = new Date();
  public get calendarDateString() {
    return format(this.calendarDate, "yyyy-MM-dd");
  }
  public set calendarDateString(val) {
    const newDate = parse(val, "yyyy-MM-dd", this.calendarDate);
    if (isValid(newDate)) {
      this.calendarDate = newDate;
    }
  }

  public filtersExpanded = false;
  public eventTypeId: number | null = null;
  public eventId: number | null = null;
  public onlyPast: boolean = false;
  public availableSeatFilter: number | null = null;

  public get hasFilters() {
    return !!this.eventTypeId || !!this.eventId || this.onlyPast;
  }
}

/**
 * Returns true if the element is in the viewport. False otherwise
 * @param el the element to check
 */
export function isInViewport(el: Element | null | undefined = document.body) {
  const rect = el?.getBoundingClientRect();

  if (!rect) {
    return false;
  }

  return (
    rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.bottom >= 0 &&
    rect.left >= 0 &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Format date to match Coalesce's expected format: "YYYY-MM-DDTHH:mm:ss.SSS" (no timezone)
 */
export function formatDateForFormData(date: Date | null | undefined): string {
  return date ? format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS") : "";
}
